import React from 'react';
import { Stack, Typography } from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { CdqExternalLead } from '@tassomutuo-backoffice/types';
import ExternalLeadCardShell from './ExternalLeadCardShell';
import { formatNumberWithThousandsSeparator } from '../../../../utils/formatMoney';

interface IProps {
  lead: CdqExternalLead;
  onOpenDetails: (id: string) => void;
}

const OCCUPATION_LABEL: Record<string, string> = {
  dipendente_pubblico: 'Dipendente Statale',
  dipendente_privato: 'Dipendente Privato',
  dipendente_parapubblico: 'Dipendente Parastatale',
  pensionato: 'Pensionato',
  autonomo: 'Lavoratore Autonomo',
};

const occupationLabel = (raw?: string) =>
  raw ? OCCUPATION_LABEL[raw] ?? raw : undefined;

const buildFullName = (lead: CdqExternalLead) =>
  [lead.name, lead.lastname].filter(Boolean).join(' ').trim();

const buildSource = (lead: CdqExternalLead) => {
  const parts: string[] = [];
  if (lead.from) parts.push(`Sorgente: ${lead.from}`);
  if (lead.agenzia_euroansa_da_cui_arriva_la_lead) {
    parts.push(`Agenzia: ${lead.agenzia_euroansa_da_cui_arriva_la_lead}`);
  }
  if (lead.consulente_richiesto_pagina) {
    parts.push(`Consulente: ${lead.consulente_richiesto_pagina}`);
  }
  if (lead.segnalatore) parts.push(`Segnalatore: ${lead.segnalatore}`);
  if (lead.request?.comune) {
    const cap = lead.request.cap ? `${lead.request.cap} ` : '';
    const prov = lead.request.provincia ? ` (${lead.request.provincia})` : '';
    parts.push(`${cap}${lead.request.comune}${prov}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
};

const buildProductInfo = (lead: CdqExternalLead) => {
  const req = lead.request;
  const refBank =
    lead.reference_bank && lead.reference_bank !== '*'
      ? lead.reference_bank
      : undefined;

  if (!req && !refBank) return null;

  const occupazione = occupationLabel(req?.occupazione);
  const hasImporto = !!req?.importo;
  const hasOccupazione = !!occupazione;
  const hasContract =
    !!req?.indeterminato || !!req?.tempo || !!req?.nDipendenti;

  if (!hasImporto && !hasOccupazione && !hasContract && !refBank) return null;

  const contractSummary = (() => {
    if (!hasContract) return undefined;
    const parts: string[] = [];
    if (req?.indeterminato === 'si') parts.push('Tempo indeterminato');
    if (req?.indeterminato === 'no') parts.push('Tempo determinato');
    if (req?.tempo) parts.push(req.tempo);
    if (req?.nDipendenti) parts.push(req.nDipendenti);
    return parts.join(' · ');
  })();

  return (
    <Stack spacing={0.5}>
      {(hasImporto || hasOccupazione) && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
        >
          {hasImporto && (
            <>
              <EuroIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={500}>
                {formatNumberWithThousandsSeparator(String(req?.importo))}
              </Typography>
            </>
          )}
          {hasOccupazione && (
            <Typography variant="body2" color="text.secondary">
              {hasImporto ? ' · ' : ''}
              {occupazione}
            </Typography>
          )}
        </Stack>
      )}
      {contractSummary && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <WorkOutlineIcon fontSize="small" color="primary" />
          <Typography variant="body2" color="text.secondary">
            {contractSummary}
          </Typography>
        </Stack>
      )}
      {refBank && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccountBalanceIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={500}>
            Banca di riferimento: {refBank}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const ExternalLeadCardCdq = ({ lead, onOpenDetails }: IProps) => {
  return (
    <ExternalLeadCardShell
      status={lead.status}
      quality={lead.quality}
      createdAt={lead.createdAt}
      fullName={buildFullName(lead)}
      email={lead.email}
      prefix={lead.prefix}
      phone={lead.phone}
      source={buildSource(lead)}
      productInfo={buildProductInfo(lead)}
      onOpenDetails={() => onOpenDetails(lead.id)}
    />
  );
};

export default ExternalLeadCardCdq;
